/**
 * SettingsPageV2 — Suite-standard settings hub (UI composition only).
 *
 * This file is a PURE COMPOSER. It does not transform data. All mapping
 * goes through `adapters/settingsAdapter.ts`. All data fetching lives
 * in dedicated hooks (`useMembersTab`, `useBillingTabFacts`,
 * `useNotificationPreferences`).
 *
 * Sections rendered (via `@levelup/ui-management/settings`):
 *   - ProfileSection           — Central-owned editing (open Central with returnUrl)
 *   - WorkspaceSection         — Central-owned editing (open Central with returnUrl)
 *   - Members                  — Central-direct CRUD via membershipApi (Bearer)
 *   - BillingSection           — BeHeard RTK Query → Stripe portal
 *   - NotificationPreferences  — Real Central fetch (loading / error / not-configured / ready)
 *   - Role & Access (extra)    — BeHeard-specific app role display + permission rows
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { NotificationPreferencesSection, type SettingsTabConfig } from '@levelup/ui-management/settings';
import { useAfterCentralChange } from '../services/centralClients';
import { selectBootApps } from '../store/slices/bootContextSlice';
import { createBeHeardCentralFetcher } from '../services/centralBillingFetcher';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';

import { selectBootCurrentWorkspace } from '../store/slices/bootContextSlice';
import { useCentralAuth } from '../hooks/useCentralAuth';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { useAppRole } from '../hooks/useAppRole';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { AppRoleBadgeList } from '../components/AppRoleBadge';
import { SuggestedRoleBundles } from '../components/SuggestedRoleBundles';
import { CENTRAL_URL } from '../config/centralAuth';
import { getSimulatedCentralRole } from '../auth/simulatedCentralRole';
import { getSimulatedAppProfile } from '../auth/simulatedAppProfile';

import { buildNotificationsSectionProps, workspacePurposeOf, isNotificationsNotConfigured } from '../adapters/settingsAdapter';

// ──────────────────────────────────────────────────────────────
// Tab catalog
// ──────────────────────────────────────────────────────────────
import { useParams } from 'react-router-dom';
import { selectCentralUser } from '../store/slices/centralAuthSlice';
import { UniversalSettings } from '../components/settings/UniversalSettings';
import { MyReferralPage } from './MyReferralPage';
import { UsageDashboardPage } from './UsageDashboardPage';
import { PublicProfileSettingsPage } from './PublicProfileSettingsPage';

const ROLE_TAB: SettingsTabConfig = { id: 'role', label: 'Role & Access', order: 600 };
const REFERRALS_TAB: SettingsTabConfig = { id: 'referrals', label: 'Referrals', order: 610 };
const USAGE_TAB: SettingsTabConfig = { id: 'usage', label: 'Usage', order: 620 };
const PUBLIC_PROFILE_TAB: SettingsTabConfig = { id: 'public-profile', label: 'Public profile', order: 630 };

/**
 * Settings v2 (owner decision D13): the universal Settings structure composed
 * from the SHARED sections (components/settings/UniversalSettings.tsx over
 * @levelup/ui-management >= 1.21.7), CRM pattern — then BeHeard's own tabs:
 * Role & Access, Referrals, Usage, Public profile (they used to be separate
 * pages at the same URLs, which keep working).
 *
 * BeHeard supplies its Central transport (browser-direct; terminal verdicts go
 * through the app's registered failure path), its switch-context refresh and
 * sign-out, and its notification preferences. Billing is titled "Billing" with
 * no credits card; its plan picker is outcomes-first and pre-ticks `?outcome=`.
 *
 * URL: /settings/<tab>[?view=<view>][&outcome=<code>]; old ids keep working.
 */
export function SettingsPageV2() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const token = useSelector(selectCentralAccessToken);
  const workspace = useSelector(selectBootCurrentWorkspace) as Record<string, unknown> | null;
  const apps = useSelector(selectBootApps) as unknown as Array<Record<string, unknown>> | undefined;
  const user = useSelector(selectCentralUser) as unknown as Record<string, unknown> | null;
  const { hasWorkspacePermission } = useCentralConsumer();
  const { logout } = useCentralAuth();
  const afterChange = useAfterCentralChange();

  // The Bearer is read at SEND time; the fetcher stays stable.
  const tokenRef = useLatestToken(token);
  const fetcher = useMemo(() => createBeHeardCentralFetcher(() => tokenRef.current), [tokenRef]);

  const search = new URLSearchParams(location.search);
  const onNavigate = useCallback(
    (next: string, view?: string | null, extra?: { outcome?: string | null }) => {
      const q = new URLSearchParams();
      if (view) q.set('view', view);
      if (extra?.outcome) q.set('outcome', extra.outcome);
      const qs = q.toString();
      navigate(`/settings/${next}${qs ? `?${qs}` : ''}`, { replace: true });
    },
    [navigate],
  );

  const appTabs = useMemo(
    () => [
      { ...ROLE_TAB, render: () => <RoleAccessTab /> },
      { ...REFERRALS_TAB, render: () => <MyReferralPage /> },
      { ...USAGE_TAB, render: () => <UsageDashboardPage /> },
      { ...PUBLIC_PROFILE_TAB, render: () => <PublicProfileSettingsPage /> },
    ],
    [],
  );

  const [origin] = useState(() => window.location.origin);
  if (!workspace?.id) return null;
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

  return (
    <UniversalSettings
      fetcher={fetcher as never}
      centralUrl={(CENTRAL_URL || '').replace(/\/+$/, '')}
      appId="beheard"
      workspace={{
        id: String(workspace.id),
        name: str(workspace.name) ?? '',
        slug: str(workspace.slug),
        role: str(workspace.role) ?? 'viewer',
        type: str(workspace.type),
        purpose: workspacePurposeOf(workspace as never),
        avatarUrl: str(workspace.avatarUrl),
        status: str(workspace.status),
        createdAt: str(workspace.createdAt),
        permissions: {
          manageMembers: hasWorkspacePermission('manageMembers'),
          manageBilling: hasWorkspacePermission('manageBilling'),
          transferOwnership: hasWorkspacePermission('transferOwnership'),
        },
      }}
      user={{
        id: String(user?.id ?? ''),
        email: String(user?.email ?? ''),
        name: str(user?.fullName) ?? str(user?.name),
        avatarUrl: str(user?.avatarUrl),
      }}
      apps={(apps ?? []).map((a) => ({ appId: String(a.appId ?? a.id ?? ''), displayName: str(a.displayName) ?? str(a.name) }))}
      activeTab={tab || 'profile'}
      view={search.get('view')}
      outcome={search.get('outcome')}
      onNavigate={onNavigate}
      returnTo={(t, v) => `${origin}/settings/${t}${v ? `?view=${encodeURIComponent(v)}` : ''}`}
      // BeHeard's fetcher already routes a terminal Central verdict through the
      // app's registered failure path (the app-wide gate owns the screen).
      onUnauthenticated={() => undefined}
      onChanged={afterChange}
      onSignOut={() => void logout()}
      overrides={{ notifications: () => <NotificationsTab /> }}
      appTabs={appTabs}
    />
  );
}

/** The latest token in a ref, written after render (read only at send time). */
function useLatestToken(token: string | null) {
  const ref = useRef(token);
  useEffect(() => {
    ref.current = token;
  }, [token]);
  return ref;
}

function NotificationsTab() {
  const { state, updateCategory } = useNotificationPreferences();
  const props = buildNotificationsSectionProps({
    state,
    onUpdateCategory: updateCategory,
  });
  return (
    <>
      {isNotificationsNotConfigured(state) && <NoCategoriesNotice />}
      <NotificationPreferencesSection {...props} />
    </>
  );
}

function NoCategoriesNotice() {
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex gap-3">
      <svg
        className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
      <div>
        <p className="text-sm font-medium text-blue-900">No notification categories yet</p>
        <p className="text-xs text-blue-800/80 mt-0.5">
          Notification categories will appear here as soon as Central publishes them
          for your workspace. You can return to this tab to configure delivery channels
          (inbox, email, push) per category once they are available.
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Role & Access tab — BeHeard-specific; preserves existing displays.
// ──────────────────────────────────────────────────────────────

function RoleAccessTab() {
  const workspace = useSelector(selectBootCurrentWorkspace);
  const { hasWorkspacePermission } = useCentralConsumer();
  const appRole = useAppRole();
  const canManageMembers = hasWorkspacePermission('manageMembers');

  // Surface Testing-Mode simulation status (UI-only — backend remains
  // authoritative on every API call). Read at render time so a Clear
  // performed in another tab is reflected on the next mount/reload.
  const simulatedRole = getSimulatedCentralRole();
  const simulatedProfile = getSimulatedAppProfile();
  const isSimulating = Boolean(simulatedRole || simulatedProfile);

  return (
    <div className="space-y-6">
      {isSimulating && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-amber-900">Testing Mode active</p>
          <p className="text-xs text-amber-800/80 mt-1 leading-snug">
            You're previewing this app as
            {simulatedRole ? <> Central role <strong>{simulatedRole}</strong></> : ' your real Central role'}
            {simulatedProfile && simulatedProfile.length > 0 && (
              <> with BeHeard profile bundle <strong>{simulatedProfile.join(', ')}</strong></>
            )}
            . Backend authorization is unchanged — the server still enforces your real capabilities.
            Clear it from the testing-mode panel in the navbar.
          </p>
        </div>
      )}

      {appRole && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Your BeHeard Access</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-2">Central Role</p>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                {(workspace?.role as string) ?? 'unknown'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-2">App Role Profile(s)</p>
              <AppRoleBadgeList profileKeys={appRole.profiles} size="md" />
            </div>
            <p className="text-sm text-gray-600">{appRole.summary}</p>
          </div>
        </div>
      )}

      {workspace?.permissions && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Your Permissions</h3>
          <div className="space-y-3">
            <PermissionRow label="Manage Members" granted={workspace.permissions.manageMembers} />
            <PermissionRow label="Manage Billing" granted={workspace.permissions.manageBilling} />
            <PermissionRow label="Transfer Ownership" granted={workspace.permissions.transferOwnership} />
          </div>
        </div>
      )}

      {canManageMembers && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900">Role Assignment Guide</h3>
          <p className="text-xs text-gray-500 mt-0.5 mb-4">
            Assign these scope bundles to workspace members in Central.
          </p>
          <SuggestedRoleBundles />
        </div>
      )}
    </div>
  );
}

function PermissionRow({ label, granted }: { label: string; granted: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-700">{label}</span>
      {granted ? (
        <span className="flex items-center gap-1 text-xs text-green-700 font-medium">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Granted
        </span>
      ) : (
        <span className="text-xs text-gray-400">Not granted</span>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Optional suite tabs (shared sections) — composition only
// ──────────────────────────────────────────────────────────────
